import {
  BatteryCharging,
  Boxes,
  CircleGauge,
  Cog,
  Disc3,
  RadioTower,
  Snowflake,
  Thermometer,
} from "lucide-react";
import type { Sensor, Vehicle } from "../types";

const systems = [
  { key: "engine", label: "Motor / CAN", icon: Cog, value: "J1939 OK" },
  { key: "brakes", label: "Frenos", icon: Disc3, value: "8.2 bar" },
  { key: "tires", label: "Llantas / TPMS", icon: CircleGauge, value: "12 activas" },
  { key: "fuel", label: "Combustible", icon: BatteryCharging, value: "Nivel estable" },
  { key: "cargo", label: "Carga", icon: Boxes, value: "Sellada" },
  { key: "reefer", label: "Ambiente", icon: Snowflake, value: "3.8 °C" },
  { key: "temperature", label: "Mazas", icon: Thermometer, value: "46 °C" },
  { key: "comms", label: "Comunicaciones", icon: RadioTower, value: "LTE · GNSS" },
];

const componentPrefixes: Record<string, string[]> = {
  engine: ["engine_", "transmission_", "can_"],
  brakes: ["brake_", "air_brake_"],
  tires: ["tire_"],
  fuel: ["fuel_"],
  cargo: ["cargo_", "door_", "trailer_"],
  reefer: ["reefer_", "cargo_zone_"],
  temperature: ["hub_", "temperature_"],
  comms: ["comms_", "gnss_", "cellular_"],
};

function sensorForSystem(key: string, sensors: Sensor[]) {
  return sensors.find((sensor) =>
    (componentPrefixes[key] || []).some((prefix) => sensor.component_code.startsWith(prefix)),
  );
}

function statusFor(key: string, vehicle: Vehicle, sensors: Sensor[]) {
  const sensor = sensorForSystem(key, sensors);
  if (sensor) return sensor.severity;
  if (key === "comms" && vehicle.status === "offline") return "critical";
  if (key === "fuel" && vehicle.fuel_percent !== null && vehicle.fuel_percent < 45) return "warning";
  return "normal";
}

export function SystemsMatrix({
  vehicle,
  sensors,
  selectedSensorId,
  onSensorSelect,
  onOpenTires,
  onOpenFuel,
}: {
  vehicle: Vehicle;
  sensors: Sensor[];
  selectedSensorId: number | null;
  onSensorSelect: (sensor: Sensor) => void;
  onOpenTires: () => void;
  onOpenFuel: () => void;
}) {
  const vehicleSensors = sensors.filter((sensor) => sensor.vehicle_id === vehicle.id);
  return (
    <section className="systems-panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">MATRIZ DE SALUD · {vehicle.unit_code}</span>
          <h2>Sistemas instrumentados</h2>
        </div>
        <span className="systems-panel__score">
          <b>{vehicle.status === "critical" ? 62 : vehicle.status === "warning" ? 79 : 94}</b>
          <small>/100</small>
        </span>
      </header>
      <div className="systems-grid">
        {systems.map(({ key, label, icon: Icon, value }) => {
          const status = statusFor(key, vehicle, vehicleSensors);
          const sensor = sensorForSystem(key, vehicleSensors);
          return (
            <button
              type="button"
              className={`system-card system-card--${status}${sensor?.id === selectedSensorId ? " is-selected" : ""}`}
              key={key}
              disabled={!sensor}
              aria-pressed={sensor?.id === selectedSensorId}
              onClick={() => {
                if (sensor) onSensorSelect(sensor);
                if (key === "tires") onOpenTires();
                if (key === "fuel") onOpenFuel();
              }}
              title={sensor ? `Inspeccionar ${sensor.component_code.replaceAll("_", " ")}` : "Sin sensor asociado"}
            >
              <span><Icon size={16} /></span>
              <div>
                <small>{label}</small>
                <strong>{sensor?.value ?? value}{sensor ? ` ${sensor.unit}` : ""}</strong>
              </div>
              <i />
            </button>
          );
        })}
      </div>
      <footer>
        <span><i className="status-dot is-online" /> 11 dispositivos enlazados</span>
        <span>Último paquete <b>ahora</b></span>
      </footer>
    </section>
  );
}
